/**
 * diamondbackPreload - OPTIONAL fast REST API fetch to seed initial preloader data
 *
 * Fetches critical data from Diamondback API to populate preloader (FALLBACK):
 * - Mempool info
 * - Fee recommendations
 * - Difficulty adjustment data
 * - Latest block data
 *
 * Priority: WebSocket data is PRIMARY, Diamondback is FALLBACK/enhancement
 * - Has 5-second timeout to prevent blocking if Diamondback is slow
 * - Returns empty data on timeout (WebSocket will provide data)
 * - Non-blocking - UI renders with WebSocket data first
 */

const DIAMONDBACK_BASE_URL = 'https://2140data.com/api';
const PRELOAD_TIMEOUT = 5000; // 5 second timeout - don't block UI

/**
 * Fetch with timeout wrapper
 * @param {string} url - URL to fetch
 * @param {AbortSignal} signal - Abort signal for timeout
 * @returns {Promise<Response>}
 */
const fetchWithTimeout = (url, signal) => fetch(url, { signal });

/**
 * Fetch all preloader data in parallel with timeout
 * FALLBACK only - WebSocket data takes priority
 * @returns {Promise<Object>} { mempoolInfo, fees, da, lastMempoolBlockData }
 */
export const fetchPreloaderData = async () => {
  console.log('[Diamondback Preload] Fetching fallback data (5s timeout)...');
  const startTime = Date.now();

  // Set up abort controller for timeout
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), PRELOAD_TIMEOUT);

  try {
    // Note: NOT fetching blocks - the /v1/blocks/{N} endpoint returns blocks from Genesis,
    // not the latest blocks. WebSocket provides recent blocks instead.
    const [mempoolResponse, feesResponse, daResponse] = await Promise.all([
      fetchWithTimeout(`${DIAMONDBACK_BASE_URL}/mempool`, controller.signal),
      fetchWithTimeout(`${DIAMONDBACK_BASE_URL}/v1/fees/recommended`, controller.signal),
      fetchWithTimeout(`${DIAMONDBACK_BASE_URL}/v1/difficulty-adjustment`, controller.signal)
    ]);

    clearTimeout(timeoutId);

    // Check all responses OK
    if (!mempoolResponse.ok || !feesResponse.ok || !daResponse.ok) {
      throw new Error('One or more Diamondback endpoints failed');
    }

    // Parse JSON
    const [mempoolData, feesData, daData] = await Promise.all([
      mempoolResponse.json(),
      feesResponse.json(),
      daResponse.json()
    ]);

    const elapsed = Date.now() - startTime;
    console.log(`[Diamondback Preload] Fallback data ready in ${elapsed}ms`);

    return {
      mempoolInfo: mempoolData,
      fees: feesData,
      da: daData,
      lastMempoolBlockData: null // WebSocket provides this
    };
  } catch (error) {
    clearTimeout(timeoutId);

    const isTimeout = error.name === 'AbortError';
    if (isTimeout) {
      console.log('[Diamondback Preload] Timed out - WebSocket will provide data');
    } else {
      console.warn('[Diamondback Preload] Failed (non-critical):', error.message);
    }

    // Return empty - WebSocket is primary source
    return {
      mempoolInfo: null,
      fees: null,
      da: null,
      lastMempoolBlockData: null
    };
  }
};
